import React, { useMemo, useState, createElement, Component } from 'react';
import { type ComponentType } from 'react';
import { DownloadIcon, FileSpreadsheetIcon, FileTextIcon, PrinterIcon, ReceiptIcon, ShieldAlertIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useData } from '../../store/DataContext';
import { Invoice, Patient, SalesReceipt, WalkInClient } from '../../types';
import { calculateInvoiceTotals, calculateSalesReceiptTotals, formatCurrency } from './utils';
type DateRangePreset = 'today' | 'this-week' | 'this-month' | 'this-year' | 'custom';
type ReportPeriod = {
  startDate: string;
  endDate: string;
};
type TaxReportTransaction = {
  date: string;
  source: 'Invoice' | 'Sales Receipt';
  documentNumber: string;
  customer: string;
  taxableAmount: number;
  totalTax: number;
  grossTotal: number;
};
type TaxReportData = {
  transactions: TaxReportTransaction[];
  invoiceTotals: TaxReportTotals;
  salesReceiptTotals: TaxReportTotals;
  totals: TaxReportTotals;
};
type TaxReportTotals = {
  transactionCount: number;
  taxableAmount: number;
  totalTax: number;
  grossTotal: number;
};
export function Reports() {
  const {
    currentUser,
    invoices,
    patients,
    salesReceipts,
    walkInClients
  } = useData();
  const [dateRange, setDateRange] = useState<DateRangePreset>('this-month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const period = useMemo(() => resolveReportPeriod(dateRange, startDate, endDate), [dateRange, endDate, startDate]);
  const report = useMemo(() => {
    if (!hasGenerated || !period) return null;
    return buildTaxReport({
      invoices,
      patients,
      salesReceipts,
      walkInClients,
      ...period
    });
  }, [hasGenerated, invoices, patients, period, salesReceipts, walkInClients]);
  if (currentUser?.role !== 'Administrator') {
    return <Card data-id="element-1731" data-mp="1ldhu46k9mz4q">
        <div className="flex flex-col items-center gap-3 py-12 text-center" role="alert" data-id="element-1732" data-mp="1ldzlopvzgp6b">
          <ShieldAlertIcon className="h-8 w-8 text-red-600" aria-hidden="true" data-id="element-1733" data-mp="1qd2sofvix5gx" />
          <div data-id="element-1734" data-mp="2e2eyd5rn6veh">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1735" data-mp="2dzsqt64r4fw9">
              Administrator access required
            </h2>
            <p className="mt-1 text-sm text-slate-600" data-id="element-1736" data-mp="251h3pe33g08k">
              Tax reports are available only to billing administrators.
            </p>
          </div>
        </div>
      </Card>;
  }
  const updateRange = (value: DateRangePreset) => {
    setDateRange(value);
    setHasGenerated(false);
    setValidationError(null);
  };
  const generateReport = () => {
    if (dateRange === 'custom' && (!startDate || !endDate)) {
      setValidationError('Choose both a start date and an end date.');
      return;
    }
    if (!period) {
      setValidationError('Enter a valid reporting period.');
      return;
    }
    if (period.startDate > period.endDate) {
      setValidationError('The start date must be on or before the end date.');
      return;
    }
    setValidationError(null);
    setHasGenerated(true);
  };
  const handleCsvExport = () => {
    if (!report || !period) return;
    const rows = [['Date', 'Source', 'Document Number', 'Customer / Bill To', 'Taxable Sales (ZMW)', 'TOT Tax 5% (ZMW)', 'Gross Total (ZMW)'], ...report.transactions.map(transaction => [transaction.date, transaction.source, transaction.documentNumber, transaction.customer, transaction.taxableAmount.toFixed(2), transaction.totalTax.toFixed(2), transaction.grossTotal.toFixed(2)]), [], ['Totals', '', '', '', report.totals.taxableAmount.toFixed(2), report.totals.totalTax.toFixed(2), report.totals.grossTotal.toFixed(2)]];
    const csv = rows.map(row => row.map(escapeCsvValue).join(',')).join('\n');
    const file = new Blob([csv], {
      type: 'text/csv;charset=utf-8'
    });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BMED_Tax_Report_${period.startDate}_to_${period.endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const handlePrint = () => {
    if (!report || !period) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setValidationError('Your browser blocked the print window. Please allow pop-ups and try again.');
      return;
    }
    printWindow.document.write(createPrintableReport(report, period));
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    window.setTimeout(() => printWindow.print(), 250);
  };
  return <div className="space-y-6" data-id="element-1737" data-mp="192qsnmofhqd6">
      <section className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1738" data-mp="144yr00xzepvs">
        <div data-id="element-1739" data-mp="z6nfzmvya9bz">
          <p className="text-sm font-semibold text-pink-600" data-id="element-1740" data-mp="z76xj2houvph">
            Billing administration
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1741" data-mp="1wt4akkdau8u1">
            Tax Report
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600" data-id="element-1742" data-mp="1rsgjxzszfu0c">
            Report 5% TOT tax on paid invoices and independent sales receipts.
          </p>
        </div>
        <span className="inline-flex w-fit items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800" data-id="element-1743" data-mp="1it3850hdytcg">
          Administrator only
        </span>
      </section>

      <Card data-id="element-1744" data-mp="7denifrlu6et">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:items-end" data-id="element-1745" data-mp="7gsmrf4z1zi1">
          <Select label="Reporting period" value={dateRange} onChange={event => updateRange(event.target.value as DateRangePreset)} options={[{
          value: 'today',
          label: 'Today'
        }, {
          value: 'this-week',
          label: 'This week'
        }, {
          value: 'this-month',
          label: 'This month'
        }, {
          value: 'this-year',
          label: 'This year'
        }, {
          value: 'custom',
          label: 'Custom range'
        }]} data-id="element-1746" data-mp="8wep1dia433o" />
          {dateRange === 'custom' ? <>
              <Input label="Start date" type="date" value={startDate} onChange={event => {
            setStartDate(event.target.value);
            setHasGenerated(false);
            setValidationError(null);
          }} data-id="element-1747" data-mp="33e9qsludng4p" />
              <Input label="End date" type="date" value={endDate} onChange={event => {
            setEndDate(event.target.value);
            setHasGenerated(false);
            setValidationError(null);
          }} data-id="element-1748" data-mp="2xqeks1i5td7n" />
            </> : <div className="md:col-span-2" data-id="element-1749" data-mp="32opveega3rqx">
              <p className="text-sm font-medium text-slate-700" data-id="element-1750" data-mp="3gg4ndas5z3a4">
                Selected dates
              </p>
              <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700" data-id="element-1751" data-mp="3cej4n8sy7us0">
                {period ? formatPeriod(period) : 'Choose a valid period'}
              </p>
            </div>}
          <Button onClick={generateReport} className="w-full" data-id="element-1752" data-mp="3ldczatmpyb2x">
            <FileSpreadsheetIcon className="mr-1.5 h-4 w-4" data-id="element-1753" data-mp="wlhdmd81oce0" /> Generate report
          </Button>
        </div>
        {validationError && <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-id="element-1754" data-mp="10pyti1baa5ji">
            {validationError}
          </p>}
      </Card>

      {report && period ? <section className="space-y-6" aria-live="polite" data-id="element-1755" data-mp="czkyq31bdsl8">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6" data-id="element-1756" data-mp="hxmgmkn07lv4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1757" data-mp="hv9zg9p2lfj4">
              <div data-id="element-1758" data-mp="muwdglcq2zsz">
                <p className="text-sm font-semibold text-pink-600" data-id="element-1759" data-mp="rn63009xdxuq">
                  Generated tax report
                </p>
                <h3 className="mt-1 text-xl font-bold text-slate-950" data-id="element-1760" data-mp="3qcenmzfywiwk">
                  TOT Tax Collection Summary
                </h3>
                <p className="mt-1 text-sm text-slate-600" data-id="element-1761" data-mp="3vag55fxqwa5x">
                  {formatPeriod(period)} · Paid invoices by invoice date and
                  cash sales by visit date.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 print:hidden" data-id="element-1762" data-mp="3duu2ur1iq1n6">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-1763" data-mp="3dszgd8alm1gz">
                  <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-1764" data-mp="38upn538fpay0" /> Print
                </Button>
                <Button size="sm" onClick={handleCsvExport} data-id="element-1765" data-mp="34ue4tx3jnmpp">
                  <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-1766" data-mp="303fiafrypse0" /> Export CSV
                </Button>
              </div>
            </div>
            <p className="mt-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900" data-id="element-1767" data-mp="2v3jg8m2tsb13">
              Source note: tax figures are recalculated from current document
              items using the single 5% TOT tax policy. Credit notes,
              quotations, and unpaid or partially paid invoices are excluded.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1768" data-mp="1vh3vfz8medt7">
            <SummaryCard label="Taxable sales" value={formatCurrency(report.totals.taxableAmount)} icon={FileTextIcon} tone="blue" data-id="element-1769" data-mp="1vf0vonho16ba" />
            <SummaryCard label="TOT tax collected" value={formatCurrency(report.totals.totalTax)} icon={FileSpreadsheetIcon} tone="emerald" data-id="element-1770" data-mp="258zylopkcu4a" />
            <SummaryCard label="Gross total" value={formatCurrency(report.totals.grossTotal)} icon={ReceiptIcon} tone="slate" data-id="element-1771" data-mp="17j4wz5olvx8g" />
            <SummaryCard label="Transactions" value={String(report.totals.transactionCount)} icon={FileTextIcon} tone="amber" data-id="element-1772" data-mp="1lgl7asa0wb5h" />
          </div>

          <Card title="Source breakdown" subtitle="Taxable sales and tax collected by recorded source." data-id="element-1773" data-mp="2swrjvht30hhp">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-1774" data-mp="30f7js2b8vckx">
              <SourceBreakdown title="Paid invoices" totals={report.invoiceTotals} data-id="element-1775" data-mp="35fmv6obyua09" />
              <SourceBreakdown title="Sales receipts" totals={report.salesReceiptTotals} data-id="element-1776" data-mp="2qj4guu8kj9wx" />
            </div>
          </Card>

          <Card noPadding data-id="element-1777" data-mp="3j5yqew3ukovm">
            <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1778" data-mp="3j7t8m2nqi2w0">
              <div data-id="element-1779" data-mp="39gowrbtcuy5e">
                <h3 className="font-bold text-slate-900" data-id="element-1780" data-mp="39h8gmy2kfwm2">Transaction ledger</h3>
                <p className="mt-0.5 text-sm text-slate-500" data-id="element-1781" data-mp="3efs0msxlmybs">
                  {report.totals.transactionCount} eligible recorded transaction
                  {report.totals.transactionCount === 1 ? '' : 's'} in the
                  selected period.
                </p>
              </div>
              <span className="text-sm font-semibold text-slate-700" data-id="element-1782" data-mp="asj9n3f55dsg">
                Tax collected: {formatCurrency(report.totals.totalTax)}
              </span>
            </div>
            {report.transactions.length ? <div className="overflow-x-auto" data-id="element-1783" data-mp="blh9qppdlngu">
                <table className="min-w-[940px] w-full divide-y divide-slate-200" data-id="element-1784" data-mp="bk5xs2ls19en">
                  <caption className="sr-only" data-id="element-1785" data-mp="6me536db5j9e">
                    Tax-report transactions for {formatPeriod(period)}
                  </caption>
                  <thead className="bg-slate-50" data-id="element-1786" data-mp="3t2gx0lwk0vdy">
                    <tr data-id="element-1787" data-mp="3t0xj31ydewfc">
                      {['Date', 'Source', 'Document number', 'Customer / Bill to', 'Taxable amount', 'TOT Tax (5%)', 'Gross total'].map(heading => <th key={heading} scope="col" className={`px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 ${['Taxable amount', 'TOT Tax (5%)', 'Gross total'].includes(heading) ? 'text-right' : 'text-left'}`} data-id="element-1788" data-mp="3a3crvo84vlrk">
                          {heading}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1789" data-mp="16vch1he69q26">
                    {report.transactions.map(transaction => <tr key={`${transaction.source}-${transaction.documentNumber}`} data-id="element-1790" data-mp="1buytvpq394ag">
                        <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-1791" data-mp="1fto7njf1gnum">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-1792" data-mp="o434ez93ul6d">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${transaction.source === 'Invoice' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-1793" data-mp="s5odsamfkhbt">
                            {transaction.source}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 font-mono text-sm font-semibold text-slate-800" data-id="element-1794" data-mp="it7jtq5u3jy2">
                          {transaction.documentNumber}
                        </td>
                        <td className="px-5 py-3.5 text-sm font-medium text-slate-900" data-id="element-1795" data-mp="rtvtk8o72y55">
                          {transaction.customer}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm text-slate-700" data-id="element-1796" data-mp="11pqljueb65kl">
                          {formatCurrency(transaction.taxableAmount)}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-emerald-700" data-id="element-1797" data-mp="33hvjpnmehz3r">
                          {formatCurrency(transaction.totalTax)}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-bold text-slate-950" data-id="element-1798" data-mp="1e2ksjekhww8b">
                          {formatCurrency(transaction.grossTotal)}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="border-t-2 border-slate-300 bg-slate-50" data-id="element-1799" data-mp="197x41fphs1tq">
                    <tr data-id="element-1800" data-mp="v976y02oa25d">
                      <th scope="row" colSpan={4} className="px-5 py-3 text-left text-sm font-bold text-slate-900" data-id="element-1801" data-mp="v8p3ojhlenny">
                        Report total
                      </th>
                      <td className="px-5 py-3 text-right text-sm font-bold text-slate-900" data-id="element-1802" data-mp="qbowg590bev9">
                        {formatCurrency(report.totals.taxableAmount)}
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-bold text-emerald-700" data-id="element-1803" data-mp="2knviy07cpi2v">
                        {formatCurrency(report.totals.totalTax)}
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-bold text-slate-950" data-id="element-1804" data-mp="2e8eta89h970x">
                        {formatCurrency(report.totals.grossTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div> : <div className="flex flex-col items-center px-5 py-14 text-center" data-id="element-1805" data-mp="31yilzkl4nmd5">
                <FileSpreadsheetIcon className="h-9 w-9 text-slate-300" aria-hidden="true" data-id="element-1806" data-mp="2wzfsmxeif4l0" />
                <h4 className="mt-3 font-semibold text-slate-900" data-id="element-1807" data-mp="2s6uzzz7caxj2">
                  No eligible transactions in this period
                </h4>
                <p className="mt-1 max-w-md text-sm leading-6 text-slate-500" data-id="element-1808" data-mp="3koqn5x59o2qp">
                  Try another date range. This report only includes paid
                  invoices and independent sales receipts.
                </p>
              </div>}
          </Card>
        </section> : <Card noPadding data-id="element-1809" data-mp="3s7zrvd2tvd9f">
          <div className="flex flex-col items-center px-5 py-14 text-center" data-id="element-1810" data-mp="3s9avncw9wl0j">
            <FileSpreadsheetIcon className="h-11 w-11 text-slate-300" aria-hidden="true" data-id="element-1811" data-mp="11f6a8vjkxjs" />
            <h3 className="mt-4 text-lg font-semibold text-slate-900" data-id="element-1812" data-mp="61uigx437z06">
              Generate a tax report
            </h3>
            <p className="mt-1 max-w-md text-sm leading-6 text-slate-500" data-id="element-1813" data-mp="ay1rwzvozsmv">
              Select a preset or custom reporting period to review taxable sales
              and 5% TOT tax collected.
            </p>
          </div>
        </Card>}
    </div>;
}
type SummaryCardProps = {
  label: string;
  value: string;
  icon: ComponentType<{
    className?: string;
  }>;
  tone: 'blue' | 'emerald' | 'slate' | 'amber';
};
function SummaryCard({
  label,
  value,
  icon: Icon,
  tone
}: SummaryCardProps) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    slate: 'bg-slate-100 text-slate-700 ring-slate-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100'
  };
  return <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-id="element-1814" data-mp="1t0akqjoe8d94">
      <div className="flex items-start justify-between gap-3" data-id="element-1815" data-mp="1o1r0cnpg79iv">
        <div data-id="element-1816" data-mp="1o0fv0jgcttpn">
          <p className="text-sm font-medium text-slate-500" data-id="element-1817" data-mp="10co9cp8xc4bp">{label}</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1818" data-mp="10cy1ojhfynkk">
            {value}
          </p>
        </div>
        <div className={`rounded-lg p-2.5 ring-1 ${tones[tone]}`} aria-hidden="true" data-id="element-1819" data-mp="1a2hizkpia9db">
          <Icon className="h-5 w-5" data-id="element-1820" data-mp="15b99koo6eo05" />
        </div>
      </div>
    </div>;
}
function SourceBreakdown({
  title,
  totals
}: {
  title: string;
  totals: TaxReportTotals;
}) {
  return <section className="rounded-lg border border-slate-200 bg-slate-50 p-4" aria-label={title} data-id="element-1821" data-mp="cndefoe9945h">
      <div className="flex items-center justify-between gap-4" data-id="element-1822" data-mp="2o9xctctlgyg3">
        <h4 className="font-semibold text-slate-900" data-id="element-1823" data-mp="2o8ma7gbvxwqk">{title}</h4>
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200" data-id="element-1824" data-mp="2t1yy1o8qpzi9">
          {totals.transactionCount} transaction
          {totals.transactionCount === 1 ? '' : 's'}
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3 text-sm" data-id="element-1825" data-mp="37yg7ub1a762p">
        <div data-id="element-1826" data-mp="37yy0yysnd46e">
          <dt className="text-slate-500" data-id="element-1827" data-mp="37wbu71dkysp8">Taxable sales</dt>
          <dd className="mt-1 font-semibold text-slate-900" data-id="element-1828" data-mp="3bz04ldhy07ob">
            {formatCurrency(totals.taxableAmount)}
          </dd>
        </div>
        <div data-id="element-1829" data-mp="2y2cx4gsd5z45">
          <dt className="text-slate-500" data-id="element-1830" data-mp="2y2mq8d8pgme2">TOT tax</dt>
          <dd className="mt-1 font-semibold text-emerald-700" data-id="element-1831" data-mp="32y1ympny1q33">
            {formatCurrency(totals.totalTax)}
          </dd>
        </div>
        <div data-id="element-1832" data-mp="1mng4yy0vutsk">
          <dt className="text-slate-500" data-id="element-1833" data-mp="1rk6knk8rzndg">Gross total</dt>
          <dd className="mt-1 font-semibold text-slate-900" data-id="element-1834" data-mp="1rlrd94bj4w8o">
            {formatCurrency(totals.grossTotal)}
          </dd>
        </div>
      </dl>
    </section>;
}
function buildTaxReport({
  invoices,
  patients,
  salesReceipts,
  walkInClients,
  startDate,
  endDate
}: {
  invoices: Invoice[];
  patients: Patient[];
  salesReceipts: SalesReceipt[];
  walkInClients: WalkInClient[];
  startDate: string;
  endDate: string;
}): TaxReportData {
  const invoiceTransactions = invoices.filter(invoice => invoice.status === 'Paid' && isDateWithinRange(invoice.date, startDate, endDate)).map(invoice => {
    const totals = calculateInvoiceTotals(invoice.items);
    return {
      date: toDateKey(invoice.date),
      source: 'Invoice' as const,
      documentNumber: invoice.invoiceNumber,
      customer: getInvoiceCustomerName(invoice, patients, walkInClients),
      taxableAmount: totals.taxableAmount,
      totalTax: totals.totalTax,
      grossTotal: totals.totalAmount
    };
  });
  const salesReceiptTransactions = salesReceipts.filter(receipt => isDateWithinRange(receipt.visitDate, startDate, endDate)).map(receipt => {
    const totals = calculateSalesReceiptTotals(receipt.items);
    return {
      date: toDateKey(receipt.visitDate),
      source: 'Sales Receipt' as const,
      documentNumber: receipt.salesReceiptNumber,
      customer: receipt.billTo,
      taxableAmount: totals.taxableAmount,
      totalTax: totals.totalTax,
      grossTotal: totals.totalAmount
    };
  });
  const transactions = [...invoiceTransactions, ...salesReceiptTransactions].sort((left, right) => right.date.localeCompare(left.date));
  const invoiceTotals = sumTransactions(invoiceTransactions);
  const salesReceiptTotals = sumTransactions(salesReceiptTransactions);
  return {
    transactions,
    invoiceTotals,
    salesReceiptTotals,
    totals: sumTransactions(transactions)
  };
}
function sumTransactions(transactions: TaxReportTransaction[]): TaxReportTotals {
  return transactions.reduce((totals, transaction) => ({
    transactionCount: totals.transactionCount + 1,
    taxableAmount: totals.taxableAmount + transaction.taxableAmount,
    totalTax: totals.totalTax + transaction.totalTax,
    grossTotal: totals.grossTotal + transaction.grossTotal
  }), {
    transactionCount: 0,
    taxableAmount: 0,
    totalTax: 0,
    grossTotal: 0
  });
}
function getInvoiceCustomerName(invoice: Invoice, patients: Patient[], walkInClients: WalkInClient[]) {
  if (invoice.customerType === 'Patient') {
    const patient = patients.find(entry => entry.id === invoice.customerId);
    return patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient';
  }
  return invoice.customerName || walkInClients.find(entry => entry.id === invoice.customerId)?.name || 'Walk-in client';
}
function resolveReportPeriod(dateRange: DateRangePreset, startDate: string, endDate: string): ReportPeriod | null {
  if (dateRange === 'custom') {
    return startDate && endDate ? {
      startDate,
      endDate
    } : null;
  }
  const today = new Date();
  const endOfToday = toDateInputValue(today);
  if (dateRange === 'today') {
    return {
      startDate: endOfToday,
      endDate: endOfToday
    };
  }
  if (dateRange === 'this-week') {
    const start = new Date(today);
    const weekday = start.getDay() || 7;
    start.setDate(start.getDate() - weekday + 1);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return {
      startDate: toDateInputValue(start),
      endDate: toDateInputValue(end)
    };
  }
  if (dateRange === 'this-year') {
    return {
      startDate: `${today.getFullYear()}-01-01`,
      endDate: `${today.getFullYear()}-12-31`
    };
  }
  return {
    startDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`,
    endDate: toDateInputValue(new Date(today.getFullYear(), today.getMonth() + 1, 0))
  };
}
function isDateWithinRange(value: string, startDate: string, endDate: string) {
  const dateKey = toDateKey(value);
  return dateKey >= startDate && dateKey <= endDate;
}
function toDateKey(value: string) {
  return value.slice(0, 10);
}
function toDateInputValue(date: Date) {
  const adjusted = new Date(date);
  adjusted.setMinutes(adjusted.getMinutes() - adjusted.getTimezoneOffset());
  return adjusted.toISOString().slice(0, 10);
}
function formatPeriod(period: ReportPeriod) {
  if (period.startDate === period.endDate) return formatDate(period.startDate);
  return `${formatDate(period.startDate)} – ${formatDate(period.endDate)}`;
}
function formatDate(value: string) {
  const date = new Date(`${toDateKey(value)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
function escapeCsvValue(value: string) {
  return `"${value.replace(/"/g, '""')}"`;
}
function createPrintableReport(report: TaxReportData, period: ReportPeriod) {
  const rows = report.transactions.map(transaction => '<tr>' + '<td>' + escapeHtml(formatDate(transaction.date)) + '</td>' + '<td>' + escapeHtml(transaction.source) + '</td>' + '<td>' + escapeHtml(transaction.documentNumber) + '</td>' + '<td>' + escapeHtml(transaction.customer) + '</td>' + '<td class="amount">' + escapeHtml(formatCurrency(transaction.taxableAmount)) + '</td>' + '<td class="amount">' + escapeHtml(formatCurrency(transaction.totalTax)) + '</td>' + '<td class="amount">' + escapeHtml(formatCurrency(transaction.grossTotal)) + '</td></tr>').join('');
  const styles = ['@page { size: A4; margin: 17mm 12mm; }', 'body { color: #0f172a; font-family: Arial, sans-serif; font-size: 11px; line-height: 1.4; }', 'h1 { margin: 0; font-size: 22px; } h2 { margin: 4px 0 0; font-size: 14px; color: #475569; }', '.header { display: flex; justify-content: space-between; gap: 20px; padding-bottom: 16px; border-bottom: 2px solid #0f172a; }', '.meta { text-align: right; color: #475569; } .note { margin: 16px 0; padding: 10px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; }', '.summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }', '.summary div { border: 1px solid #cbd5e1; padding: 10px; } .label { color: #64748b; font-size: 10px; } .value { margin-top: 4px; font-weight: bold; font-size: 14px; }', 'table { width: 100%; border-collapse: collapse; margin-top: 12px; } th { background: #f1f5f9; text-align: left; } th, td { border: 1px solid #cbd5e1; padding: 7px; vertical-align: top; } .amount { text-align: right; white-space: nowrap; }', 'tfoot { font-weight: bold; background: #f8fafc; } footer { margin-top: 24px; border-top: 1px solid #cbd5e1; padding-top: 8px; color: #64748b; font-size: 10px; }'].join('\n');
  const title = 'BMED Tax Report ' + escapeHtml(period.startDate) + ' to ' + escapeHtml(period.endDate);
  const summary = '<section class="summary">' + '<div><div class="label">Taxable sales</div><div class="value">' + escapeHtml(formatCurrency(report.totals.taxableAmount)) + '</div></div>' + '<div><div class="label">TOT tax collected</div><div class="value">' + escapeHtml(formatCurrency(report.totals.totalTax)) + '</div></div>' + '<div><div class="label">Gross total</div><div class="value">' + escapeHtml(formatCurrency(report.totals.grossTotal)) + '</div></div>' + '<div><div class="label">Transactions</div><div class="value">' + report.totals.transactionCount + '</div></div></section>';
  const ledgerRows = rows || '<tr><td colspan="7">No eligible transactions in this period.</td></tr>';
  return ['<!doctype html>', '<html lang="en">', '<head>', '<meta charset="utf-8" />', '<title>' + title + '</title>', '<style>{`' + styles + '`}</style>', '</head>', '<body>', '<header class="header"><div><h1>BMED Medical Centre</h1><h2>TOT Tax Collection Report</h2></div><div class="meta"><strong>Reporting period</strong><br />' + escapeHtml(formatPeriod(period)) + '</div></header>', '<p class="note">Includes paid invoices by invoice date and independent sales receipts by visit date. Tax is recalculated from document items at the aggregate 5% TOT rate.</p>', summary, '<table><thead><tr><th>Date</th><th>Source</th><th>Document number</th><th>Customer / Bill to</th><th class="amount">Taxable amount</th><th class="amount">TOT Tax (5%)</th><th class="amount">Gross total</th></tr></thead><tbody>' + ledgerRows + '</tbody><tfoot><tr><td colspan="4">Report total</td><td class="amount">' + escapeHtml(formatCurrency(report.totals.taxableAmount)) + '</td><td class="amount">' + escapeHtml(formatCurrency(report.totals.totalTax)) + '</td><td class="amount">' + escapeHtml(formatCurrency(report.totals.grossTotal)) + '</td></tr></tfoot></table>', '<footer>Generated from BMED Medical Centre billing records.</footer>', '</body>', '</html>'].join('\n');
}
function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}